import React from 'react'
import PropTypes from 'prop-types'

import { Person, SocialCall, SocialEmailOpen } from '@fs/zion-icon'
import { ActionChip, colors, Row, List, ListItem, QuickGlanceOverlay, useOverlay } from '@fs/zion-ui'

import NotAvailableText from '../NotAvailableText/NotAvailableText'
import transl from './CameraOperators.transl'

/**
 * @typedef CameraOperator
 * @type {Object}
 * @property {string} id - Operator ID
 * @property {string} name - Operator name
 * @property {string} email - Operator email
 * @property {number[]} phoneNumbers - List of phone numbers
 */

/**
 * Displays camera operators for a camera project
 * @param {CameraOperator[]} operators - Contains an array of objects that each contain a name, email, and an array of phone numbers as strings
 */
const PersonIcon = () => <Person color={colors.gray100} role="none" />
const translId = () => transl.id

export default function CameraOperators({ operators }) {
  const overlay = useOverlay()
  const [currentOperator, setCurrentOperator] = React.useState(() => {
    return { id: '', name: '', email: '', phoneNumbers: [] }
  })

  function onClick(operatorInfo, event) {
    setCurrentOperator(operatorInfo)
    overlay.handleClick(event)
  }

  return (
    <Row wrap>
      {operators.map((operatorInfo) => {
        return (
          <ActionChip
            Icon={PersonIcon}
            onClick={(event) => {
              onClick(operatorInfo, event)
            }}
            key={`${operatorInfo.id}-chip`}
            label={operatorInfo.name}
          />
        )
      })}
      <QuickGlanceOverlay {...overlay} title={currentOperator.name}>
        <List>
          <ListItem
            data-testid="camera-op-id"
            primaryText={currentOperator.id || ''}
            startElement={<ListItem.Icon Icon={translId} />}
          />
          <ListItem
            data-testid="camera-op-email"
            primaryText={currentOperator.email || ''}
            startElement={<ListItem.Icon Icon={SocialEmailOpen} color={colors.gray100} />}
          />
          {currentOperator.phoneNumbers && currentOperator.phoneNumbers.length > 0 ? (
            currentOperator.phoneNumbers.map((number) => {
              return (
                <ListItem
                  data-testid="camera-op-phone"
                  key={`${currentOperator}-${number}`}
                  primaryText={number}
                  startElement={<ListItem.Icon Icon={SocialCall} color={colors.gray100} />}
                />
              )
            })
          ) : (
            <ListItem
              data-testid="camera-op-phone"
              primaryText={<NotAvailableText />}
              startElement={<ListItem.Icon Icon={SocialCall} color={colors.gray100} />}
            />
          )}
        </List>
      </QuickGlanceOverlay>
    </Row>
  )
}

CameraOperators.propTypes = {
  operators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      email: PropTypes.string,
      name: PropTypes.string,
      phoneNumbers: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.node]),
    })
  ),
}
